import { useDispatch, useSelector } from "react-redux";
import { getErrorMessage } from "../../../utils/errorUtils";
import logger from "../../../utils/logger";
import { useMemo } from "react";

const initialState = {
	paymentTokenResponse: null,
	collectJS: null,
	errors: [],
	isCCFieldsAvailable: false
};

const collectJSReducer = (state = initialState, action) => {
	const { type, payload } = action;
	switch (type) {
		case 'UPDATE_PAYMENT_TOKEN_RESPONSE':
			return {
				...state,
				paymentTokenResponse: payload
			};
		case 'UPDATE_COLLECTJS':
			return {
				...state,
				collectJS: payload
			};
		case 'UPDATE_IS_CC_FIELDS_AVAILABLE':
			return {
				...state,
				isCCFieldsAvailable: payload
			};
		case 'ADD_COLLECTJS_ERROR':
			return {
				...state,
				errors: state.errors.concat([{
					fieldName: payload.fieldName,
					message: payload.message
				}])
			};
		case 'UNSET_COLLECTJS_ERRORS':
			return {
				...state,
				errors: state.errors.filter(error => error.fieldName !== payload.fieldName)
			};
		case 'CLEAR_COLLECTJS':
			return {
				...state,
				paymentTokenResponse: null,
				errors: []
			};
		case 'RESET_COLLECTJS':
			return {
				...state,
				paymentTokenResponse: null,
				errors: [],
				isCCFieldsAvailable: false
			};
		default:
			return state;
	}
};

export const setPaymentTokenResponse = (paymentTokenResponse) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'UPDATE_PAYMENT_TOKEN_RESPONSE', payload: paymentTokenResponse });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setCollectJS = (collectJSConnection) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'UPDATE_COLLECTJS', payload: collectJSConnection });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setIsCCFieldsAvailable = (isCCFieldsAvailable) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'UPDATE_IS_CC_FIELDS_AVAILABLE', payload: isCCFieldsAvailable });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const addError = (fieldName, message) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'ADD_COLLECTJS_ERROR', payload: { fieldName: fieldName, message: message } });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const unsetErrors = (fieldName) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'UNSET_COLLECTJS_ERRORS', payload: { fieldName: fieldName } });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const resetCollectJS = () => {
	return async (dispatch, getState) => {
		try {
			const state = getState();
			const collectJSState = { ...state.collectJSState };
			const { collectJS } = collectJSState;

			try {
				if (collectJS) {
					collectJS.retokenize();
				}
			} catch (err) {
				logger.logErrorEvent('COLLECTJS_RETOKENIZE_FAILED', err, getErrorMessage(err), true);
			}

			dispatch({ type: 'RESET_COLLECTJS', payload: null });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const clearCollectJS = () => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'CLEAR_COLLECTJS', payload: null });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const useCollectJS = () => {
	const dispatch = useDispatch();

	//#region useSelectors

	const errors = useSelector((appState) => appState.collectJSState.errors);

	//#endregion
	//#region useMemos 

	const isCCFieldsValid = useMemo(() => {
		// console.log('cc errors', errors);
		return errors?.length === 0;
	}, [errors]);

	//#endregion

	return {
		CollectJSState: useSelector((appState) => appState.collectJSState),

		paymentTokenResponse: useSelector((appState) => appState.collectJSState.paymentTokenResponse),
		setPaymentTokenResponse: (paymentTokenResponse) => dispatch(setPaymentTokenResponse(paymentTokenResponse)),
		collectJS: useSelector((appState) => appState.collectJSState.collectJS),
		setCollectJS: (collectJSConnection) => dispatch(setCollectJS(collectJSConnection)),
		errors: useSelector((appState) => appState.collectJSState.errors),
		addError: (fieldName, message) => dispatch(addError(fieldName, message)),
		unsetErrors: (fieldName) => dispatch(unsetErrors(fieldName)),
		resetCollectJS: () => dispatch(resetCollectJS()),
		clearCollectJS: () => dispatch(clearCollectJS()),
		isCCFieldsAvailable: useSelector((appState) => appState.collectJSState.isCCFieldsAvailable),
		setIsCCFieldsAvailable: (isCCFieldsAvailable) => dispatch(setIsCCFieldsAvailable(isCCFieldsAvailable)),
		isCCFieldsValid: isCCFieldsValid
	};
};

export default collectJSReducer;